<template>
  <div :class="{ [$style.brandedListing]: isBrandedListingEnabled }">
    <GoogleOneTap v-if="isGoogleOneTapEnabled" :language="currentLocale" />
    <div v-if="isPlatformIs24()" :class="$style.bcsWrapper" data-test="bcs-wrapper">
      <HgBreadcrumb
        :links="breadCrumbs"
        :theme="platform"
        @breadCrumbClick="trackBreadCrumbClick"
      />
    </div>
    <div v-if="isLoading">
      <div key="placeholder" :class="$style.loadingPlaceholder">
        <div :class="$style.loadingPlaceholderFill" />
      </div>
    </div>
    <ListingNotFound v-else-if="isListingNotFound || isListingGone" />
    <div v-else-if="hasListing">
      <DetailPageMeta :listingIdFromRoute="listingIdFromRoute" />
      <AdSlot
        v-if="isBrandedListingEnabled && !(isLayoutSolutionEnabled && checkIsInIframe())"
        :adUnitId="
          isMobile
            ? adsConfig.adUnits.MOBILE.PDP_BRANDED_LISTING.adUnitId[currentLocale]
            : adsConfig.adUnits.DESKTOP.PDP_BRANDED_LISTING.adUnitId[currentLocale]
        "
        :position="
          isMobile
            ? adsConfig.adUnits.MOBILE.PDP_BRANDED_LISTING.position
            : adsConfig.adUnits.DESKTOP.PDP_BRANDED_LISTING.position
        "
        additionalCssClass="centered"
        :class="$style.brandedTopAd"
        class="hide-on-print"
      />
      <div :class="[$style.detailPageLayoutWrapper]">
        <div
          :class="[
            $style.detailPageContent,
            { [$style.detailPageContentWide]: !displayAdsAreEnabled() },
          ]"
        >
          <ControlBar
            @shareButtonClick="openShareModal"
            @printButtonClick="trackOpenPrint"
            @redirectToLoginPage="handleRedirectToLoginPageForFavourites"
          />
          <div :class="$style.listingImageGalleryWrapper">
            <ListingMediaGallery
              :listingImages="listingImages"
              :virtualTour="virtualTour"
              :videoMovie="videoMovie"
              :shouldShowNewConstructionBadge="shouldShowNewConstructionBadge"
            />
          </div>
          <ContactedOnMessage
            v-if="contactedDate && isMobile"
            :contactedDate="contactedDate"
            :showDescription="false"
          />
          <div :class="$style.detailPage">
            <ListingDetails
              v-if="listing"
              :listing="listing"
              :agencyAgent="agencyAgent"
              :listerBranding="listerBranding"
              :isTenantPlusSubscriber="isTenantPlusSubscriber"
              @virtualTourClick="trackListingAccessoryClick(InteractionGoal.GO_TO_VIRTUAL_TOUR)"
              @videoClick="trackListingAccessoryClick(InteractionGoal.GO_TO_VIDEO)"
              @documentClick="trackListingAccessoryClick(InteractionGoal.GO_TO_DOCUMENT)"
              @trackOutboundLink="
                trackOutboundLink(listing, listingType, newConstructionProject, $event)
              "
              @onRequestAddress="handleRequestAddress"
              @phoneNumberListerClickBlock="onListerContactPhoneNumberClickBlock"
              @phoneNumberViewingClickBlock="onViewingContactPhoneNumberClickBlock"
              @phoneNumberRevealViewing="
                trackContactCallLead($event, ContactActionType.REVEAL_NUMBER_VIEWING)
              "
              @revealedPhoneNumberClickViewing="
                trackContactCallLead($event, ContactActionType.CALL_VIEWING)
              "
              @phoneNumberClickMobileViewing="
                trackContactCallLead($event, ContactActionType.CALL_VIEWING)
              "
              @phoneNumberRevealContact="
                trackContactCallLead($event, ContactActionType.REVEAL_NUMBER_LISTER)
              "
              @revealedPhoneNumberClickContact="
                trackContactCallLead($event, ContactActionType.CALL_LISTER)
              "
              @phoneNumberClickMobileContact="
                trackContactCallLead($event, ContactActionType.CALL_LISTER)
              "
              @maskedContentClicked="handleDescriptionContactInformationBlocked"
              @scrollToOtherListings="scrollToOtherListingsSection"
              @savePriceDropAlert="handleSavePriceDropAlert($event as CreatePriceDropAlertPayload)"
            >
              <template #spotlight>
                <UbsWidget
                  v-if="!isLayoutSolutionEnabled && shouldDisplayMortgageWidget"
                  :listing="listing"
                />
              </template>
              <template #address>
                <PartnerListAvailability v-if="!isLayoutSolutionEnabled" :listing="listing" />
              </template>
              <template #financing>
                <PartnerListFinance v-if="!isLayoutSolutionEnabled" :listing="listing" />
                <PartnerCollapsibleContainer
                  v-if="!isPlatformIs24() && !isLayoutSolutionEnabled"
                  :listing="listing"
                />
              </template>
              <template #features>
                <PartnerCollapsibleContainer
                  v-if="!isLayoutSolutionEnabled"
                  :listing="listing"
                  :isGgaMaurIntegrationEnabled="isGgaMaurIntegrationEnabled && !isPlatformIs24()"
                  :isHouseholdInsuranceIntegrationEnabled="true"
                  :isISPIntegrationEnabled="shouldDisplayISPIntegration"
                  testData="commercial-integrations"
                />
              </template>
            </ListingDetails>
            <section
              ref="contactFormContainer"
              :class="[
                $style.contactForm,
                $style.contactFormSticky,
                $style[withAddress],
                $style[withExtraInfo],
              ]"
              class="hide-on-print"
            >
              <div id="contactFormScrollAnchor" :class="$style.contactFormScrollAnchor" />
              <ListerBrandingBox
                v-if="isBrandedListingEnabled && listerBranding && !shouldShowAgencyCard"
                :listerBranding="listerBranding"
                :offerType="listing?.offerType"
                :class="$style.listerBrandingBox"
              />
              <ContactForm
                :listing="listing"
                :agencyAgent="agencyAgent"
                :listerBranding="listerBranding"
                :shouldShowAgencyCard="shouldShowAgencyCard"
                :isAddressRequested="false"
                :isTenantPlusSubscriber="isTenantPlusSubscriber"
                @formSubmitSuccess="handleSubmitSuccess"
                @contactFormMounted="onContactFormMounted"
                @openPrivacyDeclarationModalVisible="openPrivacyDeclarationModal"
                @formSubmitBlocked="handleContactFormSubmitBlocked"
                @formSubmitError="trackContactFormLeadError"
              />
              <ContactBlockingTime
                v-if="isContactBlocked"
                :contactFormBlockTime="contactFormBlockTime"
                :entryPoint="getEntryPoint"
              ></ContactBlockingTime>
            </section>
          </div>
        </div>
        <div
          v-if="displayAdsAreEnabled()"
          :class="$style.skyscraperAdContainer"
          class="hide-on-print"
        >
          <AdSlot
            v-if="!isMobile"
            :adUnitId="adsConfig.adUnits.DESKTOP.PDP_OUTSIDE_RIGHT.adUnitId[currentLocale]"
            :position="adsConfig.adUnits.DESKTOP.PDP_OUTSIDE_RIGHT.position"
            :class="$style.skyscraperAd"
          />
        </div>
      </div>
      <section
        v-if="shouldShowNewConstructionRecommendations"
        id="otherListings"
        :class="$style.listingTeaserSection"
      >
        <NewConstructionRecommendations
          :newConstructionProject="newConstructionProject"
          :title="t('newConstruction.furtherApartments')"
          shouldShowNewConstructionBadge
        />
      </section>
      <section v-else-if="shouldShowRecommendations" :class="$style.listingTeaserSection">
        <LazyListingRecommendations
          :class="$style.listingRecommendations"
          :listingIds="listingIdsForRecommendations"
          :recommendationsVersion="recommendationsVersion"
          :title="t('recommendations.detailPageTitle')"
          :gtmPageType="GTMPageType.DETAIL_PAGE"
          :apiBaseUrl="apiBaseUrl"
          :listingApiEndpoint="listingApiEndpoint"
          :nextButtonTitle="t('recommendations.nextButtonTitle')"
          :prevButtonTitle="t('recommendations.previousButtonTitle')"
          :language="locale"
          :listingPictureDefaultAltText="t('listingPictureDefaultAltText')"
          :recommendationsPriceOnRequestText="t('recommendations.priceOnRequest')"
          :shouldInjectAcrHeaders="shouldInjectAcrHeaders()"
          :getAcrHeaders="getAcrHeaders"
          :generatePdpRoute="getPdpRoute"
          :theme="platform"
        />
      </section>
      <section :class="$style.fraudWidget" class="hide-on-print">
        <FraudReportWidget
          v-if="listing"
          :listing="listing"
          @reportButtonClick="trackFraudReportActionEvent"
          @formSubmitSuccess="trackFraudReportSubmit"
        />
      </section>
      <ServiceBoxesContainer
        v-if="!isLayoutSolutionEnabled && serviceBoxes && serviceBoxes.length"
        :serviceBoxes="serviceBoxes"
        :class="$style.serviceBoxes"
      />
      <AdSlot
        :adUnitId="
          isMobile
            ? adsConfig.adUnits.MOBILE.PDP_FOOTER.adUnitId[currentLocale]
            : adsConfig.adUnits.DESKTOP.PDP_FOOTER.adUnitId[currentLocale]
        "
        :position="
          isMobile
            ? adsConfig.adUnits.MOBILE.PDP_FOOTER.position
            : adsConfig.adUnits.DESKTOP.PDP_FOOTER.position
        "
        additionalCssClass="centered"
        :class="$style.bottomAd"
        class="hide-on-print"
        @ryAdSlotMounted="initAds"
      />
      <div v-if="!isPlatformIs24()" data-test="bcs-wrapper">
        <HgBreadcrumb
          :links="breadCrumbs"
          :theme="platform"
          @breadCrumbClick="trackBreadCrumbClick"
        />
      </div>
    </div>
    <ContactFormStickyCTA
      v-if="hasListing"
      :class="$style.contactFormStickyCTA"
      :isHidden="isContactFormInViewport"
      :showIcon="false"
      buttonText="contactForm.CONTACT_ADVERTISER"
      class="hide-on-print"
      @click="handleContactFormStickyCTAClick"
    />
    <SharingModal
      v-if="isShareModalLoaded"
      :isModalVisible="isShareModalVisible"
      :listingLocale="listingDefaultLocale"
      :isLayoutSolutionActive="isLayoutSolutionEnabled"
      @close="closeShareModal"
      @share="trackShareActionEvent"
    />

    <!-- Request Address Contact Form Modal -->
    <ContactFormModal
      v-if="isRequestAddressModalLoaded && listing"
      :isModalVisible="isRequestAddressModalVisible"
      :listing="listing"
      :isAddressRequested="true"
      @close="closeRequestAddressModal"
    />

    <!-- Query Param Contact Form Modal (from Favourites CTA) -->
    <ContactFormModal
      v-if="isContactFormModalOpen && listing"
      :isModalVisible="isContactFormModalOpen"
      :listing="listing"
      :isAddressRequested="false"
      @close="closeContactFormModal"
      @formSubmitStarted="handleModalSubmitStarted"
      @formSubmitSuccess="handleSubmitSuccess"
    />

    <PrivacyDeclarationModal
      v-if="isPrivacyDeclarationModalLoaded"
      :isModalVisible="isPrivacyDeclarationModalVisible"
      @close="closePrivacyDeclarationModal"
    />
    <TenantPlusModal
      v-if="isTenantPlusModalLoaded"
      :isModalOpen="isTenantPlusModalVisible"
      :modalOpenTrigger="tenantPlusModalOpenTrigger"
      :entryPoint="tenantPlusTrackingEntryPoint"
      @close="closeTenantPlusModal()"
    />
    <TenantPlusSignupConfirmation
      v-if="isTenantPlusSignupConfirmationVisible"
      :feature="tenantPlusFeature"
      @close="closeTenantPlusSignupConfirmation"
    />
    <TenantPlusEngagementModal
      v-if="isTenantPlusEngagementModalVisible"
      :translations="tenantPlusEngagementModalTranslations"
      :imageSrc="tenantPlusVisual"
      :baseUrl="platformBaseUrl"
      :entryPoint="tenantPlusTrackingEntryPoint"
      @close="isTenantPlusEngagementModalVisible = false"
    >
    </TenantPlusEngagementModal>
    <CrifModal
      v-if="isCrifModalVisible && listing"
      :listing="listing"
      :entryPoint="tenantPlusTrackingEntryPoint"
      @close="closeCrifModalForRentObject"
    />
    <FinancingStickyModal
      v-if="isFinancingModalVisible && listing"
      :listing="listing"
      @close="isFinancingModalVisible = false"
    ></FinancingStickyModal>
    <LegalProtectionStickyModal
      v-if="isLegalProtectionModalVisible && listing"
      :listing="listing"
      @close="isLegalProtectionModalVisible = false"
    ></LegalProtectionStickyModal>
    <RentalCertificateModal
      v-if="isRentalCertificateModalVisible && listing"
      :listing="listing"
      @close="isRentalCertificateModalVisible = false"
    ></RentalCertificateModal>
    <template v-if="isAddressRequestToastOpen && isSubmittedAsAddressRequest">
      <HgToast
        :isVisible="isFormSubmittedWithSuccess && !shouldShowContactRequestEmailVerificationPanel"
        :class="$style.notificationToast"
        @clear="onAddressRequestClose"
      >
        <HgNotificationMessage
          :title="t('contactForm.submitFeedback.success.title')"
          theme="success"
        >
          {{ t('contactForm.submitFeedback.success.message') }}
        </HgNotificationMessage>
      </HgToast>
      <HgToast
        :isVisible="isFormSubmittedWithError"
        :class="$style.notificationToast"
        @clear="onAddressRequestClose"
      >
        <HgNotificationMessage :title="t('contactForm.submitFeedback.error.title')" theme="danger">
          {{ t('contactForm.submitFeedback.error.message') }}
        </HgNotificationMessage>
      </HgToast>
      <HgToast
        :isVisible="isFormSubmittedWithOfflineError"
        :class="$style.notificationToast"
        @clear="onAddressRequestClose"
      >
        <HgNotificationMessage :title="t('contactForm.submitFeedback.error.title')" theme="danger">
          {{ t('contactForm.submitFeedback.error.listingOffline') }}
        </HgNotificationMessage>
      </HgToast>
    </template>
    <SigninModal
      v-if="isLoginModalVisible && listingId"
      ref="SigninModalRef"
      :isVisible="isLoginModalVisible"
      :translations="favouritesSigninModalTranslations"
      :image="favouritesSigninModalImage"
      @close="isLoginModalVisible = false"
      @signin="handleSignin"
      @register="handleRegister"
      @onModalOpened="trackLoginRequireDisplay(listingId)"
    ></SigninModal>
    <HgToast
      data-key="toastPOI"
      :isVisible="toastConfigPOI.isVisible"
      :class="$style.notificationToast"
      @clear="handleClearToastPOI"
    >
      <HgNotificationMessage :title="toastConfigPOI.title" :theme="toastConfigPOI.theme">
        {{ toastConfigPOI.description }}
      </HgNotificationMessage>
    </HgToast>
    <HgToast
      data-key="toastPriceDrop"
      :isVisible="toastConfigPriceDrop.isVisible"
      :class="$style.notificationToast"
      @clear="handleClearPriceDropToast"
    >
      <HgNotificationMessage
        :title="toastConfigPriceDrop.title"
        :theme="toastConfigPriceDrop.theme"
      >
        {{ toastConfigPriceDrop.description }}
      </HgNotificationMessage>
    </HgToast>
  </div>
</template>

<script lang="ts" setup>
/* eslint-disable max-lines */
import { datadogLogs } from '@datadog/browser-logs';
import {
  AdSlot,
  getAdsConfig,
  initRelevant,
  PageType,
  trackAlloyPageView,
} from '@smg-real-estate/ads-lib';
import {
  HgBreadcrumb,
  HgNotificationMessage,
  HgSupportedLanguage,
  HgToast,
  NotificationMessageTheme,
  SupportedPlatforms,
  type HgBreadcrumbModel,
} from '@smg-real-estate/domus';
import {
  Address,
  OfferType as HgRetsOfferType,
  L10N,
  Listing,
  OfferType,
  ProjectType,
} from '@smg-real-estate/hg-rets-schema';
import {
  AgencyAgent,
  AgencyBasePackage,
  ListerBranding,
  ListingType,
  StorageHelper,
  type ListingItem,
} from '@smg-real-estate/hg-search-libs';
import {
  LazyListingRecommendations,
  RecommendationsVersion,
  SigninModal,
  TenantPlusEngagementModal,
} from '@smg-real-estate/seeker-app-shared-components';
import { TreatmentConfig } from '@smg-real-estate/vue-experiments';
import {
  Component,
  computed,
  defineAsyncComponent,
  onBeforeMount,
  onMounted,
  onServerPrefetch,
  onUnmounted,
  onUpdated,
  provide,
  ref,
  useSSRContext,
  watch,
} from 'vue';

import tenantPlusVisual from '@/assets/TenantPlus/tenant-plus-visual.webp?url';
import ContactedOnMessage from '@/components/ContactedOnMessage/ContactedOnMessage.vue';
import {
  ContactActionType,
  getContactFormLeadAction,
  PhoneNumberType,
  trackContactCallLead,
  trackContactFormLead,
  trackContactFormLeadError,
  trackContactFormModalClose,
} from '@/components/ContactForm/contact-form-tracking-events';
import ContactForm from '@/components/ContactForm/ContactForm.vue';
import ContactFormStickyCTA from '@/components/ContactFormStickyCTA/ContactFormStickyCTA.vue';
import ControlBar from '@/components/ControlBar/ControlBar.vue';
import FraudReportWidget from '@/components/FraudReport/FraudReportWidget.vue';
import { trackGoogleOneTapLoginSuccess } from '@/components/GoogleOneTap/GoogleOneTap.tracking';
import GoogleOneTap from '@/components/GoogleOneTap/GoogleOneTap.vue';
import ListerBrandingBox from '@/components/ListerBrandingBox/ListerBrandingBox.vue';
import ListingDetails from '@/components/ListingDetails/ListingDetails.vue';
import ListingMediaGallery from '@/components/ListingMediaGallery/ListingMediaGallery.vue';
import NewConstructionRecommendations from '@/components/NewConstruction/NewConstructionRecommendations/NewConstructionRecommendations.vue';
import { isListingValidForMortgage } from '@/components/PartnerIntegrations';
import PartnerCollapsibleContainer from '@/components/PartnerIntegrations/PartnerCollapsibleContainer/PartnerCollapsibleContainer.vue';
import PartnerListAvailability from '@/components/PartnerIntegrations/PartnerListAvailability/PartnerListAvailability.vue';
import PartnerListFinance from '@/components/PartnerIntegrations/PartnerListFinance/PartnerListFinance.vue';
import UbsWidget from '@/components/PartnerIntegrations/UbsMortgage/UbsWidget.vue';
import {
  getServiceBoxes,
  ServiceBoxConfig,
} from '@/components/ServiceBoxes/helpers/serviceboxes-helpers';
import ServiceBoxesContainer from '@/components/ServiceBoxes/ServiceBoxesContainer.vue';
import ContactBlockingTime from '@/components/TenantPlus/ContactBlockingTime.vue';
import {
  TenantPlusEngagementModalTranslations,
  useTenantPlusEngagementModal,
} from '@/composables/useTenantPlusEngagementModal';
import {
  getAuthToken,
  getUserLoginInfo,
  isUserLoggedIn,
  loginWithRedirect,
} from '@/helpers/Authentication/authClient';
import {
  getRedirectState,
  RedirectAction,
  removeRedirectState,
} from '@/helpers/Authentication/redirect';
import { checkTenantPlusSubscriber } from '@/helpers/Authentication/tenantPlusAuth';
import { LoginTargetAction, ReferringFeature } from '@/helpers/Authentication/types';
import { createDetailPageBreadcrumbs } from '@/helpers/Breadcrumbs/breadcrumbs';
import { isMobileViewport } from '@/helpers/component-helpers';
import { getContactedOnDateForListing } from '@/helpers/ContactForm/ContactFormHelper';
import { getTenantPlusCookiesValue } from '@/helpers/CookiesHelper';
import FeatureFlags from '@/helpers/FeatureToggle/FeatureFlags';
import { isFeatureEnabled } from '@/helpers/FeatureToggle/FeatureToggleHelper';
import { getAcrHeaders, shouldInjectAcrHeaders } from '@/helpers/get-acr-headers';
import { isGgaMaurEnabled } from '@/helpers/GgaMaur/GgaMaurHelper';
import { getLoginStatusParameters } from '@/helpers/GTM/GTMLoginStatus';
import {
  GTMPageType,
  InteractionContainerDetail,
  InteractionContainerName,
  InteractionEvent,
  InteractionGoal,
} from '@/helpers/GTM/GTMParameters';
import { trackEventHarmonized } from '@/helpers/GTM/GTMTrack';
import { isListingEligibleForISP } from '@/helpers/ISPIntegration/ISPHelper';
import {
  getListingDefaultLocale,
  getListingDisplayPrice,
  getListingVideoMovie,
  getListingVirtualTour,
  isNonSwissBorderRegion,
} from '@/helpers/ListingData/ListingData';
import { getCurrentPlatformTheme, isPlatformIs24 } from '@/helpers/platformDetection';
import { CustomPOIRequestStatus } from '@/helpers/PointOfInterests/types';
import { getPassThroughHeaders } from '@/helpers/RequestHeaders/RequestHeaders';
import { getPdpRoute, normalizedFullPath } from '@/helpers/Router/RouterHelper';
import generateSessionId from '@/helpers/Session/generateSessionId';
import { StorageNamespace } from '@/helpers/storage-namespaces';
import { getContactFormBlockTime } from '@/helpers/TenantPlus/ContactFormBlockTime';
import { shouldOpenTenantPlusEngagementModal } from '@/helpers/TenantPlus/TenantPlusEngagement';
import {
  TenantPlusFeature,
  TenantPlusModalOpenTrigger,
} from '@/helpers/TenantPlus/TenantPlusModalOpenTriggers';
import { generateTenantPlusTrackingEntryPoint } from '@/helpers/TenantPlus/TenantPlusUrls';
import { isLayoutSolutionActive } from '@/layout-solution/layout-solution';
import {
  HgListingVideoMovie,
  HgListingVirtualTour,
  HgLocalStorageKey,
  HgSessionStorageKey,
  NewConstructionDataWithOtherListings,
} from '@/models';
import { SplitFeatureFlags } from '@/splitio/splitFeatureFlags';
import { useContactFormStore } from '@/store/modules/contact-form';
import { useFavouritesStore } from '@/store/modules/favourites';
import { useGeoStore } from '@/store/modules/geo';
import { useListerBrandingStore } from '@/store/modules/lister-branding';
import { useListingStore } from '@/store/modules/listing';
import { useNewConstructionStore } from '@/store/modules/new-construction';
import { usePriceDropAlertStore } from '@/store/modules/price-drop-alert';
import { useSplitStore } from '@/store/modules/split';
import { useTravelTimeStore } from '@/store/modules/travel-time';
import type { HgImage } from '@/store/types';
import { ContactFormSubmitStatus } from '@/store/types/contact-form-state';
import {
  CreatePriceDropAlertPayload,
  isPriceDropAlertSupportedBasePackage,
  PriceDropAlertAction,
  PriceDropAlertRequestStatus,
  PriceDropAlertSupportedChannel,
} from '@/store/types/price-drop-alert-state';
import {
  trackFavouriteRegisterAccountButton,
  trackFavouriteSignInButton,
  trackLoginRequireDisplay,
  trackLoginSuccessfulRedirection,
} from '@/trackers/Favourite/Favourite.tracking';
import { HgToastConfig } from '@/types';
import DetailPageMeta from '@/views/DetailPage/DetailPageMeta/DetailPageMeta.vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useDetailPageTracking, UseDetailPageTrackingParams } from './useDetailPageTracking';
import { useStickyModalSelector } from './useStickyModalSelector';

const FavoritesHgLoginModalImage = defineAsyncComponent(
  () => import('@/assets/favourites/hg_required_login_modal.svg'),
);
const FavoritesIs24LoginModalImage = defineAsyncComponent(
  () => import('@/assets/favourites/is24_required_login_modal.svg'),
);

const storageHelper = new StorageHelper(StorageNamespace.detailsPage);

const searchAlertStorageHelper = new StorageHelper(StorageNamespace.searchAlerts);

const splitStore = useSplitStore();
const listingStore = useListingStore();
const favouritesStore = useFavouritesStore();
const geoStore = useGeoStore();
const newConstructionStore = useNewConstructionStore();
const listerBrandingStore = useListerBrandingStore();
const travelTimeStore = useTravelTimeStore();
const priceDropAlertStore = usePriceDropAlertStore();

const route = useRoute();
const router = useRouter();
const ssrContext = import.meta.env.SSR ? useSSRContext() : undefined;
const { t, locale } = useI18n();
const contactFormStore = useContactFormStore();

defineOptions({
  name: 'DetailPage',
});

const ListingNotFound = defineAsyncComponent(
  () => import('@/components/ListingNotFound/ListingNotFound.vue'),
);
const PrivacyDeclarationModal = defineAsyncComponent(
  () => import('@/components/PrivacyDeclarationModal/PrivacyDeclarationModal.vue'),
);
const SharingModal = defineAsyncComponent(
  () => import('@/components/Sharing/SharingModal/SharingModal.vue'),
);
const ContactFormModal = defineAsyncComponent(
  () => import('@/components/CTANoAddress/ContactFormModal.vue'),
);
const CrifModal = defineAsyncComponent(
  () => import('@/components/PartnerIntegrations/CrifModal/CrifModal.vue'),
);
const FinancingStickyModal = defineAsyncComponent(
  () => import('@/components/PartnerIntegrations/FinancingStickyModal/FinancingStickyModal.vue'),
);
const LegalProtectionStickyModal = defineAsyncComponent(
  () =>
    import('@/components/PartnerIntegrations/LegalProtectionStickyModal/LegalProtectionStickyModal.vue'),
);
const RentalCertificateModal = defineAsyncComponent(
  () =>
    import('@/components/PartnerIntegrations/RentalCertificateModal/RentalCertificateModal.vue'),
);
const TenantPlusModal = defineAsyncComponent(
  () => import('@/components/TenantPlus/TenantPlusModal.vue'),
);
const TenantPlusSignupConfirmation = defineAsyncComponent(
  () => import('@/components/TenantPlus/TenantPlusSignupConfirmation.vue'),
);

export interface Props {
  listingIdFromRoute: string;
  scroll?: string;
}
const props = withDefaults(defineProps<Props>(), {
  scroll: 'false',
});

const adsConfig = getAdsConfig(getCurrentPlatformTheme()) as any;

const platformBaseUrl = import.meta.env.VUE_APP_BASE_URL as string;
const apiBaseUrl = import.meta.env.VUE_API_BASE_URL as string;
const listingApiEndpoint = import.meta.env.VUE_APP_LISTING_API_ENDPOINT as string;

const contactFormIntersectionObserver = ref<IntersectionObserver | undefined>(undefined);
const isContactFormInViewport = ref<boolean>(false);
const isPrivacyDeclarationModalVisible = ref<boolean>(false);
const isPrivacyDeclarationModalLoaded = ref<boolean>(false);
const isShareModalVisible = ref<boolean>(false);
const isShareModalLoaded = ref<boolean>(false);
const isRequestAddressModalVisible = ref<boolean>(false);
const isRequestAddressModalLoaded = ref<boolean>(false);
const isTenantPlusModalLoaded = ref<boolean>(false);
const sessionId = ref<string | undefined>(undefined);
const isFormSubmittedWithSuccess = ref<boolean>(false);
const isFormSubmittedWithError = ref<boolean>(false);
const isFormSubmittedWithOfflineError = ref<boolean>(false);
const isAddressRequestToastOpen = ref<boolean>(false);
const isCrifModalVisible = ref<boolean>(false);
const isTenantPlusEngagementModalVisible = ref<boolean>(false);
const isTenantPlusSignupConfirmationVisible = ref<boolean>(false);
const tenantPlusFeature = ref<TenantPlusFeature>(TenantPlusFeature.CONTACT_BLOCK);
const isFinancingModalVisible = ref<boolean>(false);
const isLegalProtectionModalVisible = ref<boolean>(false);
const isRentalCertificateModalVisible = ref<boolean>(false);
const tenantPlusModalOpenTrigger = ref<TenantPlusModalOpenTrigger | undefined>(undefined);
const tenantPlusTrackingEntryPoint = ref<string>('detailPage');
const isTenantPlusSubscriber = ref<boolean>(false);
const isMobile = ref<boolean>(false);
const isFormWithAddress = ref<boolean>(false);
const contactedDate = ref<string | undefined>(undefined);
const isGoogleOneTapEnabled = ref<boolean>(false);
const isLoginModalVisible = ref<boolean>(false);
const contactFormContainer = ref<Element | null>(null);
const isContactFormModalOpen = ref<boolean>(false);
const wasModalSubmitStarted = ref<boolean>(false);

const isTreatmentOn = (flag: SplitFeatureFlags) => splitStore.isTreatmentOn(flag);
const priceDropAlertStatus = computed<PriceDropAlertRequestStatus>(() => {
  return priceDropAlertStore.requestStatus;
});
const tenantPlusEngagementModalTranslations = computed<TenantPlusEngagementModalTranslations>(
  () => useTenantPlusEngagementModal(t).translations,
);
const contactFormSubmitStatus = computed<ContactFormSubmitStatus>(
  () => contactFormStore.contactFormSubmitStatus,
);
const currentLocale = computed<HgSupportedLanguage>(() => locale.value as HgSupportedLanguage);
const agencyAgent = computed<AgencyAgent | undefined>(() => listingStore.agencyAgent);
const listing = computed<Listing | undefined>(() => listingStore.listing);
const listingType = computed<ListingType | undefined>(() => listingStore.listingType?.type);
const listerBranding = computed<ListerBranding | undefined>(
  () => listerBrandingStore.listerBranding,
);
const address = computed<Address | undefined>(() => listing.value?.address);
const listingDefaultLocale = computed<L10N | null | undefined>(
  () => listingStore.listingDefaultLocale,
);
const hasListing = computed<boolean>(() => listingStore.hasListing);
const dataFetchError = computed(() => listingStore.dataFetchError);
const listingImages = computed<HgImage[] | undefined>(() => {
  const listingImages = listingStore.listingImages ?? [];
  return listingImages.length > 0 ? listingImages : undefined;
});
const isListingNotFound = computed<boolean>(
  () => listingStore.dataFetchError?.response?.status === 404,
);

const isListingGone = computed<boolean>(
  () => listingStore.dataFetchError?.response?.status === 410,
);
const getErrorData = computed(() => listingStore.dataFetchError?.response?.data);
const newConstructionProject = computed<NewConstructionDataWithOtherListings | null | undefined>(
  () => newConstructionStore.newConstructionData,
);
const shouldShowNewConstructionBadge = computed<boolean>(() => !!newConstructionProject.value);
const shouldShowNewConstructionRecommendations = computed<boolean>(() => {
  const listingIsPremiumNewConstruction =
    newConstructionProject.value?.projectType === ProjectType.NewConstructionPremium;
  const listingProjectHasOtherListings =
    !!newConstructionProject.value?.otherListings &&
    newConstructionProject.value.otherListings.length > 0;
  return listingIsPremiumNewConstruction && listingProjectHasOtherListings;
});
const shouldShowRecommendations = computed<boolean>(() => {
  return (
    !isFeatureEnabled(FeatureFlags.DISABLE_RECOMMENDATIONS, route) &&
    !isLayoutSolutionActive(route.query)
  );
});
const recommendationsVersion = computed<RecommendationsVersion>(() => RecommendationsVersion.v2);
const shouldShowAgencyCard = computed<boolean>(() => {
  const hasAgencyAgent = !!agencyAgent.value;
  const isPremiumBranding = !!listerBranding.value?.isPremiumBranding;
  const isBuyListingWithValidPackage =
    (listerBranding.value?.basePackage === AgencyBasePackage.RE_GOLD ||
      listerBranding.value?.basePackage === AgencyBasePackage.RE_WELCOME) &&
    listing.value?.offerType === HgRetsOfferType.Buy;
  return hasAgencyAgent && (isPremiumBranding || isBuyListingWithValidPackage);
});
const isLoading = computed<boolean>(() => listingStore.isLoading);
const listingIdsForRecommendations = computed<string[]>(() => [props.listingIdFromRoute]);
const breadCrumbs = computed<HgBreadcrumbModel[]>(() => {
  return isPlatformIs24() ? is24BreadCrumbs() : hgBreadCrumbs();
});
const isBrandedListingEnabled = computed<boolean>(() => {
  if (!hasListing.value || !listerBranding.value) {
    return false;
  }
  return (
    listerBranding.value.adActive === true ||
    isFeatureEnabled(FeatureFlags.ENABLE_BRANDED_LISTING, route)
  );
});
const geoTags = computed<string[]>(() => address.value?.geoTags ?? []);
const isSubmittedAsAddressRequest = computed<boolean>(
  () => contactFormStore.isSubmittedAsAddressRequest,
);
const shouldShowContactRequestEmailVerificationPanel = computed<boolean>(() => {
  return (
    contactFormSubmitStatus.value === ContactFormSubmitStatus.EMAIL_VERIFICATION_REQUIRED &&
    contactFormStore.isContactRequestEmailVerificationRequired &&
    !contactFormStore.isSubmittedByAuthenticatedUser
  );
});
const shouldDisplayMortgageWidget = computed<boolean>(() => {
  return (
    !!listing.value &&
    isListingValidForMortgage(listing.value) &&
    !isNonSwissBorderRegion(listing.value.address?.postalCode)
  );
});
const virtualTour = computed<HgListingVirtualTour | undefined>(() => {
  if (listingDefaultLocale.value == null) {
    return;
  }
  return getListingVirtualTour(listingDefaultLocale.value, locale.value);
});
const videoMovie = computed<HgListingVideoMovie | undefined>(() => {
  if (listingDefaultLocale.value == null) {
    return;
  }
  return getListingVideoMovie(listingDefaultLocale.value, locale.value);
});
const isGgaMaurIntegrationEnabled = computed<boolean>(() => {
  return (
    isFeatureEnabled(FeatureFlags.ENABLE_GGA_MAUR, route) &&
    !!listing.value?.address?.postalCode &&
    isGgaMaurEnabled(listing.value.address.postalCode, currentLocale.value)
  );
});
const shouldDisplayISPIntegration = computed<boolean>(() => {
  return (
    isSunriseIntegrationEnabled.value &&
    !!listing.value?.address?.country &&
    !!listing.value?.categories &&
    isListingEligibleForISP(listing.value.address?.country, listing.value.categories)
  );
});
const isContactBlocked = computed<boolean>(() => listing.value?.lister?.allowToContact === false);
const contactFormBlockTime = computed<string>(() => getContactFormBlockTime(listing.value));
const getEntryPoint = computed<string>(() => {
  return generateTenantPlusTrackingEntryPoint(
    listing.value?.offerType,
    TenantPlusModalOpenTrigger.CONTACT_FORM,
  );
});
const isLayoutSolutionEnabled = computed<boolean>(() => isLayoutSolutionActive(route.query));
const isTenantPlusModalVisible = computed<boolean>(
  () => tenantPlusModalOpenTrigger.value !== undefined,
);
const treatments = computed<Record<string, TreatmentConfig>>(() => splitStore.treatments);
const currentNormalizedPath = computed<string>(() => normalizedFullPath(route, router));
const withAddress = computed<string>(() => (isFormWithAddress.value ? 'withAddress' : 'noAddress'));
const withExtraInfo = computed<string>(() =>
  listing.value?.offerType === HgRetsOfferType.Buy ? 'withExtraInfo' : 'noExtraInfo',
);
const isSunriseIntegrationEnabled = computed<boolean>(() =>
  isTreatmentOn(SplitFeatureFlags.KK_1590_SUNRISE_INTEGRATION),
);
const serviceBoxes = computed<ServiceBoxConfig[]>(() => {
  if (!listing.value) {
    return [];
  }
  return getServiceBoxes(
    isPlatformIs24() ? SupportedPlatforms.is24 : SupportedPlatforms.hg,
    listing.value,
    currentLocale.value,
    /* Parameter used to pass feature flag values (e.g., ISP integration)
     */
    {
      isSunriseIntegrationEnabled: isSunriseIntegrationEnabled.value,
    },
  );
});
const listingId = computed<string | undefined>(() => listing.value?.id ?? undefined);
const favouritesSigninModalTranslations = computed<{
  title: string;
  description: string;
  signinButtonText: string;
  registerButtonText: string;
}>(() => {
  return {
    title: t('favourites.dialog.signinRequired.title'),
    description: t('favourites.dialog.signinRequired.description'),
    signinButtonText: t('favourites.dialog.signinRequired.signinBtn'),
    registerButtonText: t('favourites.dialog.signinRequired.registerBtn'),
  };
});
const favouritesSigninModalImage = computed<Component>(() => {
  return isPlatformIs24() ? FavoritesIs24LoginModalImage : FavoritesHgLoginModalImage;
});
const platform = computed<SupportedPlatforms>(() => {
  return isPlatformIs24() ? SupportedPlatforms.is24 : SupportedPlatforms.hg;
});
const toastConfigPOI = computed<HgToastConfig>(() => {
  switch (customPOIModalStatus.value) {
    case CustomPOIRequestStatus.SUCCESS:
      return {
        title: t('systemMessages.success.title'),
        theme: NotificationMessageTheme.Success,
        isVisible: true,
      };
    case CustomPOIRequestStatus.ERROR:
      return {
        title: t('systemMessages.error.title'),
        theme: NotificationMessageTheme.Danger,
        description: t('systemMessages.error.text'),
        isVisible: true,
      };
    default:
      return {
        title: '',
        theme: NotificationMessageTheme.Success,
        isVisible: false,
      };
  }
});
const customPOIModalStatus = computed<CustomPOIRequestStatus>(() => {
  return travelTimeStore.requestStatus;
});
const toastConfigPriceDrop = computed<HgToastConfig>(() => {
  switch (priceDropAlertStatus.value) {
    case PriceDropAlertRequestStatus.ACTIVATED:
      return {
        title: t('priceDropNotifications.subscribed'),
        theme: NotificationMessageTheme.Success,
        isVisible: true,
      };
    case PriceDropAlertRequestStatus.CREATED:
      return {
        title: t('priceDropNotifications.created'),
        theme: NotificationMessageTheme.Info,
        isVisible: true,
      };
    case PriceDropAlertRequestStatus.ALREADY_EXISTS_ERROR:
      return {
        title: '',
        theme: NotificationMessageTheme.Info,
        description: t('priceDropNotifications.alreadyExists'),
        isVisible: true,
      };
    case PriceDropAlertRequestStatus.DELETED:
      return {
        title: t('priceDropNotifications.unsubscribed'),
        theme: NotificationMessageTheme.Success,
        isVisible: true,
      };
    case PriceDropAlertRequestStatus.ERROR:
      return {
        title: t('systemMessages.error.title'),
        theme: NotificationMessageTheme.Danger,
        description: t('systemMessages.error.text'),
        isVisible: true,
      };
    default:
      return {
        title: '',
        theme: NotificationMessageTheme.Success,
        isVisible: false,
      };
  }
});

const { selectStickyModalToShow } = useStickyModalSelector({
  listing,
  storageHelper,
  isRentalCertificateModalVisible,
  isCrifModalVisible,
  isFinancingModalVisible,
  isLegalProtectionModalVisible,
  tenantPlusTrackingEntryPoint,
});

const {
  trackPartnerWidget,
  trackBreadCrumbClick,
  trackOpenPrint,
  trackListingAccessoryClick,
  trackOutboundLink,
  trackFraudReportActionEvent,
  trackFraudReportSubmit,
  trackShareActionEvent,
  trackShareOpen,
  trackVirtualPageView,
  trackBottomSheetDisplay,
} = useDetailPageTracking({
  listing,
  listingType,
  currentLocale,
  listingImages,
  newConstructionProject,
  dataFetchError,
  sessionId,
  normalizedFullPath: currentNormalizedPath,
  treatments,
  serviceBoxes,
  isLayoutSolutionEnabled,
  listerBranding,
  shouldShowAgencyCard,
} as UseDetailPageTrackingParams);

watch(
  () => props.listingIdFromRoute,
  async () => {
    await fetchListingIfNoError();
  },
);

watch(listing, async (newListing) => {
  if (!newListing) return;
  await pageTracking();
});

watch(currentLocale, async (newLocale, oldLocale) => {
  if (newLocale !== oldLocale && listing.value?.id) {
    await fetchListing(newLocale);
  }
  await pageTracking();
});

watch(
  geoTags,
  async () => {
    await resolveGeoLocations();
  },
  { deep: true },
);

/**
 * contactFormSubmitStatus
 */
watch(contactFormSubmitStatus, (newStatus) => {
  if (newStatus === ContactFormSubmitStatus.NOT_SUBMITTED) {
    return;
  }

  // Reset modal submit flag on failure so the next close is treated as a drop-off.
  // SUCCESS is not included here: handleSubmitSuccess resets the flag after tracking.
  if (
    newStatus === ContactFormSubmitStatus.ERROR ||
    newStatus === ContactFormSubmitStatus.LISTING_OFFLINE
  ) {
    wasModalSubmitStarted.value = false;
  }

  isFormSubmittedWithSuccess.value = newStatus === ContactFormSubmitStatus.SUCCESS;
  isFormSubmittedWithError.value = newStatus === ContactFormSubmitStatus.ERROR;
  isFormSubmittedWithOfflineError.value = newStatus === ContactFormSubmitStatus.LISTING_OFFLINE;

  if (
    newStatus === ContactFormSubmitStatus.EMAIL_VERIFICATION_REQUIRED &&
    shouldShowContactRequestEmailVerificationPanel.value
  ) {
    wasModalSubmitStarted.value = false;
  }

  isAddressRequestToastOpen.value = true;
});

onBeforeMount(() => {
  onBeforeMountHandler();
});

onMounted(async () => {
  isMobile.value = isMobileViewport();
  if (!isPlatformIs24() && getTenantPlusCookiesValue()) {
    isTenantPlusEngagementModalVisible.value = shouldOpenTenantPlusEngagementModal(
      HgLocalStorageKey.PDP_OPENED_COUNT,
    );
    tenantPlusTrackingEntryPoint.value = generateTenantPlusTrackingEntryPoint(
      listing.value?.offerType,
      TenantPlusModalOpenTrigger.BOTTOMSHEET,
    );
  }

  if (listing.value?.id) {
    contactedDate.value = getContactedOnDateForListing(listing.value.id);
  }

  if (isLayoutSolutionEnabled.value && globalThis.window !== undefined) {
    const browserWindow = globalThis.window;

    void (async () => {
      // timed retry without blocking UI to wait for the parent iframe to be ready
      const deadline = Date.now() + 3000;
      while (Date.now() < deadline && !browserWindow.parentIFrame?.sendMessage) {
        await new Promise((r) => setTimeout(r, 50));
      }

      browserWindow.parentIFrame?.sendMessage?.('layout_solution_pdp_loaded');
      browserWindow.parentIFrame?.sendMessage?.({ type: 'layout_solution_pdp_loaded' });
    })().catch(() => undefined);

    browserWindow.parent?.postMessage?.({ type: 'layout_solution_pdp_loaded' }, '*');
  }

  onMountedHandler();

  await Promise.all([setupTenantPlusSubscriber()]);
  handleRedirectAction();
  handleGoogleOneTapEnabled();
  await handlePriceDropAlertAction();
});
onUpdated(() => {
  onUpdatedHandler();
});
onUnmounted(() => {
  cleanUpContactFormObserver();
});

// Inject into ImageGallery.vue to check if it is rendered as an iframe on PPA
provide('isPreviewMode', false);

const is24BreadCrumbs = (): HgBreadcrumbModel[] => {
  if (!listing.value) {
    return [];
  }

  return createDetailPageBreadcrumbs(
    SupportedPlatforms.is24,
    listing.value,
    currentLocale.value,
    platformBaseUrl,
    (key: string) => t(key).toString(),
    (id: string) => {
      return geoStore.getGeoLocationById(id);
    },
    (id: string, lang: HgSupportedLanguage) => {
      return geoStore.getGeoLocationName(id, lang);
    },
    route,
    geoStore,
  );
};
const hgBreadCrumbs = (): HgBreadcrumbModel[] => {
  if (!listing.value) {
    return [];
  }

  return createDetailPageBreadcrumbs(
    SupportedPlatforms.hg,
    listing.value,
    currentLocale.value,
    platformBaseUrl,
    (key: string) => t(key).toString(),
    (id: string) => geoStore.getGeoLocationById(id),
    (id: string, lang: HgSupportedLanguage) => geoStore.getGeoLocationName(id, lang),
    route,
    geoStore,
  );
};
const scrollToOtherListingsSection = (): void => {
  const element = document.getElementById('otherListings');
  if (element) {
    // Set an offset of 100px from the top to still see the floating CTA
    const topPosition = element.getBoundingClientRect().top + window.pageYOffset - 100;
    window.scrollTo({ top: topPosition, behavior: 'smooth' });
  }
};
const handleContactFormStickyCTAClick = (): void => {
  const element = document.getElementById('contactFormScrollAnchor');
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};
const onBeforeMountHandler = (): void => {
  sessionId.value = storageHelper.getSessionStorageItem(HgSessionStorageKey.SESSION_ID);
  if (!sessionId.value) {
    sessionId.value = generateSessionId();
    storageHelper.setSessionStorageItem(HgSessionStorageKey.SESSION_ID, sessionId.value);
  }

  fetchListingIfNoError();
};
const onMountedHandler = async (): Promise<void> => {
  // remove t_nonce
  if (route?.query?.t_nonce) {
    await router.replace({
      query: {
        ...route.query,
        t_nonce: undefined,
      },
    });
  }

  setUpContactFormObserver();

  await pageTracking(true);
};
const onUpdatedHandler = (): void => {
  setUpContactFormObserver();
};
const pageTracking = async (checkListing = false): Promise<void> => {
  // We need here a detection of the state of the listing data because there are different behaviors in SSR mode.
  if (checkListing && !listing.value && !dataFetchError.value) {
    return;
  }

  const loginStatusTrackingParameters = await getLoginStatusParameters();
  trackVirtualPageView(loginStatusTrackingParameters);
  if (shouldDisplayMortgageWidget.value) {
    trackPartnerWidget(InteractionContainerDetail.PW_SHOWN_MP);
  }
  trackAlloyPageView(adsConfig);

  if (
    getListingDefaultLocale(listing.value)?.text?.description?.includes(
      '<span class="tenantPlusExclusive">',
    )
  ) {
    trackEventHarmonized(InteractionEvent.FEATURE_DISPLAYED, {
      h_event_action_007: 'display_description_hide_contact',
      h_event_label_008: 'tenantPlus_trial_description_contact',
    });
  }
};

const handleSubmitSuccess = (
  isFormWithAddress: boolean,
  contactInquiryId: string | undefined,
  isOwner: boolean,
  hasExtraInformation: boolean,
  isEmailVerificationRequired?: boolean,
  isFormSubmittedwithProfileId?: boolean,
) => {
  // Modal may already be closed by the time formSubmitSuccess arrives (contactRequestDispatched
  // emits close before the async response), so we only check wasModalSubmitStarted here.
  if (wasModalSubmitStarted.value) {
    trackContactFormLead(
      getContactFormLeadAction(isFormWithAddress),
      contactInquiryId,
      isOwner,
      isEmailVerificationRequired ? 'not_verified' : 'verified',
      hasExtraInformation,

      isFormSubmittedwithProfileId,
      'favs',
    );
    wasModalSubmitStarted.value = false;
    return;
  }

  const modal = selectStickyModalToShow();

  if (modal) {
    trackBottomSheetDisplay(modal.bottomSheetType);
    modal.open();
  }

  trackContactFormLead(
    getContactFormLeadAction(isFormWithAddress),
    contactInquiryId,
    isOwner,
    isEmailVerificationRequired ? 'not_verified' : 'verified',
    hasExtraInformation,

    isFormSubmittedwithProfileId,
  );
};
const handleModalSubmitStarted = () => {
  wasModalSubmitStarted.value = true;
};
const handleRequestAddress = () => {
  if (isContactBlocked.value) {
    tenantPlusTrackingEntryPoint.value = generateTenantPlusTrackingEntryPoint(
      listing.value?.offerType,
      TenantPlusModalOpenTrigger.REQUEST_ADDRESS,
    );
    openTenantPlusModal(TenantPlusModalOpenTrigger.REQUEST_ADDRESS);
  } else {
    isRequestAddressModalVisible.value = true;
    isRequestAddressModalLoaded.value = true;
  }
};
const handleContactFormSubmitBlocked = () => {
  tenantPlusTrackingEntryPoint.value = generateTenantPlusTrackingEntryPoint(
    listing.value?.offerType,
    TenantPlusModalOpenTrigger.CONTACT_FORM,
  );
  openTenantPlusModal(TenantPlusModalOpenTrigger.CONTACT_FORM);
};

const shouldShowContactFormModal = computed((): boolean => {
  if (route.query.contactForm !== 'modal' || !listing.value || isContactBlocked.value) {
    return false;
  }

  // if it's a T+ listing and the user is not a subscriber, we hide the modal
  return !(listing.value?.valueAddedServices?.isTenantPlusListing && !isTenantPlusSubscriber.value);
});

const stopWatchingContactFormModal = watch(
  shouldShowContactFormModal,
  (shouldShow) => {
    // Watch shouldShowContactFormModal and open the modal once when it becomes true
    // This handles the case where listing loads and(or) isTenantPlusSubscriber is determined asynchronously
    if (shouldShow && !isContactFormModalOpen.value) {
      isContactFormModalOpen.value = true;
    }
  },
  { immediate: true },
);

const closeContactFormModal = () => {
  if (
    !wasModalSubmitStarted.value &&
    contactFormSubmitStatus.value !== ContactFormSubmitStatus.SUCCESS &&
    !shouldShowContactRequestEmailVerificationPanel.value &&
    listing.value?.id
  ) {
    trackContactFormModalClose(listing.value.id);
  }
  stopWatchingContactFormModal(); // Stop watching to prevent modal from reopening after user closes it
  isContactFormModalOpen.value = false;
};

const openShareModal = () => {
  isShareModalVisible.value = true;
  isShareModalLoaded.value = true;
  trackShareOpen(listing.value?.id ?? '', sessionId.value ?? '');
};
const closeShareModal = () => {
  isShareModalVisible.value = false;
};
const closePrivacyDeclarationModal = () => {
  isPrivacyDeclarationModalVisible.value = false;
};
const closeRequestAddressModal = () => {
  isRequestAddressModalVisible.value = false;
};
const onContactFormMounted = () => {
  if (props.scroll === 'true') {
    const element = document.getElementById('contactFormScrollAnchor');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }
};
const openPrivacyDeclarationModal = () => {
  isPrivacyDeclarationModalVisible.value = true;
  isPrivacyDeclarationModalLoaded.value = true;
};
const openTenantPlusModal = (trigger: TenantPlusModalOpenTrigger) => {
  tenantPlusModalOpenTrigger.value = trigger;
  isTenantPlusModalLoaded.value = true;
};
const closeTenantPlusModal = () => {
  tenantPlusModalOpenTrigger.value = undefined;
};
const setupTenantPlusSubscriber = async () => {
  isTenantPlusSubscriber.value = await validateTenantPlusSubscriber();
  showTenantPlusSignupConfirmation();
};
const setUpContactFormObserver = () => {
  if (!listing.value) {
    return;
  }
  if (
    typeof IntersectionObserver === 'undefined' ||
    !contactFormContainer.value ||
    contactFormIntersectionObserver.value
  ) {
    return;
  }
  contactFormIntersectionObserver.value = new IntersectionObserver(
    (entries: IntersectionObserverEntry[]) => {
      isContactFormInViewport.value = entries[0].isIntersecting;
    },
  );
  contactFormIntersectionObserver.value.observe(contactFormContainer.value);
};
const cleanUpContactFormObserver = () => {
  if (contactFormIntersectionObserver.value) {
    contactFormIntersectionObserver.value.disconnect();
  }
};
const displayAdsAreEnabled = (): boolean => {
  return !isFeatureEnabled(FeatureFlags.DISABLE_ADS, route) && !isLayoutSolutionEnabled.value;
};
const fetchListingIfNoError = async () => {
  // Fetch the listing if...
  const noListingOrRetriableError =
    (!listing.value || // we have no listing saved in the store yet
      !!dataFetchError.value) && // or the previous fetch attemp failed
    dataFetchError.value?.response?.status !== 404; // but not if the fetch failed with 404 - in this case we know the listing is offline or does not exist and don't want to try again

  // Fetch the listing if we have an new ID in the route compared to the listing that is already fetched in the store or...
  const hasListingChanged = listing.value && props.listingIdFromRoute !== listing.value.id;

  // Fetch the listing if the fetching error in the store was for a different listing, and now we have a new one to fetch
  const hasListingChangedToPreviousError =
    dataFetchError.value && props.listingIdFromRoute !== dataFetchError.value.listingId;
  if (noListingOrRetriableError || hasListingChanged || hasListingChangedToPreviousError) {
    await fetchListing(currentLocale.value);
  }

  const listingOfferType = listingStore.listingOfferType as HgRetsOfferType;
  const offerTypeFromRoute = route.meta!.offerType as HgRetsOfferType;

  if (listingOfferType && offerTypeFromRoute && listingOfferType !== offerTypeFromRoute) {
    router.replace({
      name: `detailPage-${listingOfferType}-${currentLocale.value}`,
    });
  }
};
const fetchListing = async (languageCode: HgSupportedLanguage) => {
  const authToken = getTenantPlusCookiesValue() ? await getAuthToken() : undefined;

  // If it's SSR, we need to pass the headers so that they can check it's a bot
  if (ssrContext?.passThroughHeaders) {
    await listingStore.fetchListing({
      listingId: props.listingIdFromRoute,
      passThroughHeaders: getPassThroughHeaders(ssrContext?.passThroughHeaders),
      authToken,
      languageCode: currentLocale.value,
    });
  } else {
    await listingStore.fetchListing({
      listingId: props.listingIdFromRoute,
      authToken,
      languageCode: languageCode ? languageCode : currentLocale.value,
    });
  }
};
const getPriceDropAlert = async () => {
  await priceDropAlertStore.getPriceDropAlert(props.listingIdFromRoute);
};
const activatePriceDropAlert = async () => {
  await priceDropAlertStore.activatePriceDropAlert(props.listingIdFromRoute);
};
const deletePriceDropAlert = async () => {
  await priceDropAlertStore.deletePriceDropAlert(props.listingIdFromRoute);
};
const handlePriceDropAlertAction = async (): Promise<void> => {
  const offerTypeFromRoute = route.meta!.offerType;
  if (offerTypeFromRoute !== OfferType.Buy) {
    return;
  }

  const priceDropAction = searchAlertStorageHelper.getLocalStorageItem(
    HgLocalStorageKey.PRICE_DROP_ALERT_ACTION,
  );

  switch (priceDropAction) {
    case PriceDropAlertAction.ACTIVATE_PRICE_DROP_ALERT:
      await activatePriceDropAlert();
      break;
    case PriceDropAlertAction.DELETE_PRICE_DROP_ALERT:
      await deletePriceDropAlert();
      break;
    default:
      await getPriceDropAlert();
  }
};
const initAds = async (): Promise<void> => {
  if (isFeatureEnabled(FeatureFlags.DISABLE_ADS, route)) {
    return;
  }

  if (typeof window !== 'undefined' && listing.value) {
    try {
      await initRelevant({
        pageType: PageType.DETAIL_PAGE,
        listing: listing.value ?? undefined,
        language: currentLocale.value,
        platform: getCurrentPlatformTheme(),
        isMobile: isMobile.value,
        test: import.meta.env.VUE_STAGE !== 'prod',
        datadogLogs,
      });
    } catch (error) {
      console.log('Error initializing ads:', error);
      // Log the error but don't let ad initialization failures break the page
      datadogLogs.logger.error('Failed to initialize ads', { error });
    }
  }
};
const resolveGeoLocations = async () => {
  if (geoTags.value.length) {
    await geoStore.resolveLocationsById({
      geoLocationIds: geoTags.value,
    });
  }
};
const onAddressRequestClose = () => {
  isAddressRequestToastOpen.value = false;
};
const closeCrifModalForRentObject = () => {
  isCrifModalVisible.value = false;
};
const closeTenantPlusSignupConfirmation = () => {
  isTenantPlusSignupConfirmationVisible.value = false;

  // In order to load the geoData via serverPrefetch (lambda)
  // we have to force a hard reload.
  const url = new URL(location.href);
  url.searchParams.delete('plusSignup');
  url.searchParams.delete('plusFeature');
  location.href = url.href;
};
const showTenantPlusSignupConfirmation = () => {
  if (route.query.plusSignup !== 'true') {
    return;
  }

  switch (route.query.plusFeature) {
    case TenantPlusFeature.CONTACT_BLOCK:
    case TenantPlusFeature.LISTING_DEMAND:
    case TenantPlusFeature.EXCLUSIVE_IMAGES:
      tenantPlusFeature.value = route.query.plusFeature;
      break;
    default:
      tenantPlusFeature.value = TenantPlusFeature.CONTACT_BLOCK;
  }

  isTenantPlusSignupConfirmationVisible.value = isTenantPlusSubscriber.value;
};
const onListerContactPhoneNumberClickBlock = (): void => {
  tenantPlusTrackingEntryPoint.value = generateTenantPlusTrackingEntryPoint(
    listing.value?.offerType,
    TenantPlusModalOpenTrigger.LISTER_PHONE_NUMBER,
  );
  openTenantPlusModal(TenantPlusModalOpenTrigger.LISTER_PHONE_NUMBER);
};
const onViewingContactPhoneNumberClickBlock = (): void => {
  tenantPlusTrackingEntryPoint.value = generateTenantPlusTrackingEntryPoint(
    listing.value?.offerType,
    TenantPlusModalOpenTrigger.PHONE_NUMBER,
  );
  openTenantPlusModal(TenantPlusModalOpenTrigger.PHONE_NUMBER);
  trackContactCallLead(PhoneNumberType.VIEWING_PHONE, ContactActionType.REVEAL_NUMBER_VIEWING);
};
const handleDescriptionContactInformationBlocked = () => {
  tenantPlusTrackingEntryPoint.value = 'pdp_rent_desc_hide_contact';
  openTenantPlusModal(TenantPlusModalOpenTrigger.DESCRIPTION_CONTACT);
};
const validateTenantPlusSubscriber = async (): Promise<boolean> => {
  return getTenantPlusCookiesValue() ? await checkTenantPlusSubscriber() : false;
};
const handleRedirectAction = async (): Promise<void> => {
  const redirectState = getRedirectState();
  const loggedInUser = await getUserLoginInfo();
  if (redirectState?.redirectAction === RedirectAction.SAVE_FAVORITE && loggedInUser) {
    trackLoginSuccessfulRedirection(loggedInUser);
    await favouritesStore.saveFavourite({
      listingId: redirectState.redirectPayload?.listingIdAwaitingLogin ?? '',
      pageInteractionName: InteractionContainerName.PDP,
      listingItem: listingStore.listing as unknown as ListingItem,
    });
    removeRedirectState();
  }

  if (redirectState?.redirectAction === RedirectAction.GOOGLE_ONE_TAP && loggedInUser) {
    trackGoogleOneTapLoginSuccess(loggedInUser!.userId);
    removeRedirectState(); // clean up redirect state to avoid repeated tracking on page reload
  }
};
const handleSignin = async () => {
  await loginWithRedirect(
    LoginTargetAction.login,
    RedirectAction.SAVE_FAVORITE,
    {
      listingIdAwaitingLogin: listing.value?.id ?? '',
    },
    ReferringFeature.FAVOURITES,
  );
  trackFavouriteSignInButton(listingId.value || '');
};
const handleRegister = async () => {
  await loginWithRedirect(
    LoginTargetAction.signup,
    RedirectAction.SAVE_FAVORITE,
    {
      listingIdAwaitingLogin: listing.value?.id ?? '',
    },
    ReferringFeature.FAVOURITES,
  );
  trackFavouriteRegisterAccountButton(listingId.value || '');
};
const handleRedirectToLoginPageForFavourites = async () => {
  if (await isUserLoggedIn()) {
    return; // Favourites will be saved successfully if the user is already logged in
  }

  if (isPlatformIs24()) {
    isLoginModalVisible.value = true; // IS24 will show SignIn modal popup before going to Auth0 login page
    return;
  }

  // Unauthenticated HG user will be redirected to login page without SignIn modal popup
  await loginWithRedirect(
    LoginTargetAction.login,
    RedirectAction.SAVE_FAVORITE,
    {
      listingIdAwaitingLogin: listing.value?.id ?? '',
    },
    ReferringFeature.FAVOURITES,
  );
};
const handleClearToastPOI = (): void => {
  travelTimeStore.setCustomPOIModalRequestStatus(CustomPOIRequestStatus.NOT_SUBMITTED);
};
const handleSavePriceDropAlert = async (payload: CreatePriceDropAlertPayload): Promise<void> => {
  if (!listing.value) {
    return;
  }
  const price = getListingDisplayPrice(listing.value);
  if (!price) {
    return;
  }

  const basePackage = listerBranding.value?.basePackage;

  if (!isPriceDropAlertSupportedBasePackage(basePackage)) {
    return;
  }

  await priceDropAlertStore.createPriceDropAlert({
    recipient: payload.recipient,
    listingId: payload.listingId,
    name: payload.name,
    basePackage,
    language: currentLocale.value,
    price,
    channel: PriceDropAlertSupportedChannel.EMAIL,
  });
};
const handleClearPriceDropToast = (): void => {
  priceDropAlertStore.setPriceDropAlertStatus(PriceDropAlertRequestStatus.NOT_SUBMITTED);
};

const handleGoogleOneTapEnabled = async (): Promise<void> => {
  isGoogleOneTapEnabled.value = !(await isUserLoggedIn());
};

const checkIsInIframe = (): boolean => {
  if (globalThis.window === undefined) {
    return false;
  }

  try {
    return globalThis.window.self !== globalThis.window.top;
  } catch {
    return true;
  }
};

onServerPrefetch(async () => {
  if (!ssrContext) {
    return;
  }
  /*
    In case of T+ user, PDP is forced to render its content at client side.
    A follow-up ticket, DOL-301, was created to find a cleaner solution to which we can get rid of CSR.
     */
  if (!ssrContext.isTenantPlusSubscriber) {
    await fetchListing(currentLocale.value);
    await resolveGeoLocations();

    if (isListingNotFound.value) {
      ssrContext.notFound = 'LISTING_NOT_FOUND';
      ssrContext.notFoundData = getErrorData.value;
    }

    if (isListingGone.value) {
      ssrContext.notFound = 'LISTING_GONE';
      ssrContext.notFoundData = getErrorData.value;
    }
  }
});
</script>

<style module lang="scss">
@import '@/assets/variables';

$well-max-width: 585px;
$well-border-radius: $space-75;
$well-margin-vertical: $space-100;
$detail-blocks-spacing: $space-200;
$bottom-ad-spacing-vertical: $space-100 * 2;
$skyscraper-ad-sticky-offset: $space-50 * 1.5;
$sticky-cta-height: 68px;
$contact-form-sticky-no-address-min-height: 768px;
$contact-form-sticky-no-address-with-extra-info-min-height: 1168px;
$contact-form-sticky-no-address-with-card-min-height: 844px;
$contact-form-sticky-no-address-with-card-with-extra-info-min-height: 1244px;
$contact-form-sticky-with-address-min-height: 900px;
$contact-form-sticky-with-address-with-extra-info-min-height: 1300px;
$contact-form-sticky-with-address-with-card-min-height: 1048px;
$contact-form-sticky-with-address-with-card-with-extra-info-min-height: 1448px;
$contact-form-sticky-offset: $space-50 * 1.5;
$contact-form-width: 386px;
$detail-blocks-spacing: $space-200;
$contact-form-overlap-height: 50px;
$gallery-margin-vertical: $space-100;
$print-section-items-spacing: $space-75;

.bcsWrapper {
  margin: $space-75 0;
}

.detailPage {
  flex-flow: row wrap;
  margin-bottom: calc(#{$space-100} * 2);
}

.contactForm {
  flex: 0 0 $contact-form-width;
  z-index: 100;
}

.listingImageGalleryWrapper {
  margin: 0 (-$layout-margin-horizontal) $gallery-margin-vertical (-$layout-margin-horizontal);
}

.listingTeaserSection {
  width: 100%;
}

.listingRecommendations {
  margin-top: $detail-blocks-spacing;
  margin-bottom: $detail-blocks-spacing;
}

.fraudWidget {
  margin-bottom: $detail-blocks-spacing;
}

.contactFormScrollAnchor {
  position: relative;
  top: calc((#{$header-height-hg-sm} - #{$space-100}) * -1);
  visibility: hidden;
}

.loadingPlaceholder {
  padding: $space-100 0;
}

.loadingPlaceholderFill {
  height: 100vh;
  background-color: $color-grey-30;
}

.detailPageLayoutWrapper {
  position: relative;
  display: flex;
}

.detailPageContent {
  flex-grow: 1;
  flex-shrink: 1;
  max-width: 100%;
}

.bottomAd,
.brandedTopAd {
  margin-right: -$layout-margin-horizontal;
  margin-left: -$layout-margin-horizontal;
  text-align: center;
}

.brandedTopAd {
  min-height: 8.75rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.bottomAdStyle {
  margin-bottom: $bottom-ad-spacing-vertical;
}

.notificationToast > div {
  z-index: 1000;
  bottom: calc(#{$sticky-cta-height} + #{$space-100});
}

.serviceBoxes {
  margin-bottom: $space-200;
}

@media (min-width: $viewport-md-min) {
  .detailPage {
    display: flex;
  }

  .bcsWrapper {
    margin: $space-150 0;
  }

  .contactFormStickyCTA {
    display: none !important;
  }

  .bottomAd,
  .brandedTopAd {
    margin-right: 0;
    margin-left: 0;
  }

  .brandedTopAd {
    min-height: 11.25rem;
  }

  .notificationToast > div {
    bottom: $space-100;
  }
}

.skyscraperAdContainer {
  position: absolute;
  top: -99999px;
  left: -99999px;
  display: block;
  width: 0;
  height: 0;
  visibility: hidden;

  .skyscraperAd {
    flex-shrink: 1;
  }
}

.listerBrandingBox {
  display: none;
}

@media (min-width: $viewport-xlarge-min) {
  .listingImageGalleryWrapper {
    margin-right: 0;
    margin-left: 0;
  }

  .skyscraperAdContainer {
    position: relative;
    top: auto;
    left: auto;
    width: auto;
    height: auto;
    padding-left: $space-150;
    visibility: visible;
  }

  .brandedListing {
    .skyscraperAdContainer {
      padding-top: $space-150;
    }
  }

  .listerBrandingBox {
    display: flex;
  }
}

@media (min-width: $viewport-xlarge-min) and (max-width: ($viewport-xxlarge-min - 1)) {
  .detailPageContent {
    &:not(.detailPageContentWide) {
      max-width: calc(100% - #{$skyscraper-ad-width} - #{$space-150});
      min-width: calc(#{$viewport-md-min} - #{$space-300});
    }
  }

  .skyscraperAdContainer {
    flex-shrink: 0;
    min-height: 100%;
    padding-bottom: $bottom-ad-spacing-vertical;
  }
}

@media (min-width: $viewport-xxlarge-min) {
  .skyscraperAdContainer {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    padding-left: $space-100;
    transform: translateX(100%);
  }
}

@supports (position: sticky) {
  @mixin contact-form-sticky($minHeight) {
    @media (min-width: $viewport-md-min) and (min-height: $minHeight) {
      position: sticky;
      top: $contact-form-sticky-offset;
      align-self: flex-start;
    }
  }

  .contactFormSticky {
    &.noAddress {
      &.withExtraInfo {
        @include contact-form-sticky($contact-form-sticky-no-address-with-extra-info-min-height);
      }

      &.noExtraInfo {
        @include contact-form-sticky($contact-form-sticky-no-address-min-height);
      }
    }

    &.withAddress {
      &.withExtraInfo {
        @include contact-form-sticky($contact-form-sticky-with-address-with-extra-info-min-height);
      }

      &.noExtraInfo {
        @include contact-form-sticky($contact-form-sticky-with-address-min-height);
      }
    }
  }

  @media (min-width: $viewport-xlarge-min) {
    .skyscraperAd {
      position: sticky;
      top: 0;
      align-self: flex-start;
    }
  }
}

@media print {
  .detailPageLayoutWrapper {
    display: block;
  }

  .detailPageContent,
  .detailPageContentWide {
    max-width: 100% !important;
  }
}
</style>
